import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Eye, EyeOff, ChevronRight } from "lucide-react";
import BlueprintBackground from "../components/BlueprintBackground";
import BrandLogo from "../components/BrandLogo";
import { AvatarUpload } from "../components/Avatar";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { useAuth, formatApiError } from "../lib/auth";
import { api } from "../lib/api";
import { requestLocationPermission } from "../lib/geolocation";

export default function RegisterPage() {
    const navigate = useNavigate();
    const { user, register, updateProfilePicture } = useAuth();
    const [form, setForm] = useState({
        first_name: "",
        last_name: "",
        birthdate: "",
        company: "",
        phone: "",
        home_address: "",
        email: "",
        password: "",
    });
    const [pictureData, setPictureData] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (user) navigate("/home", { replace: true });
    }, [user, navigate]);

    const bind = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const isValid =
        form.first_name.trim().length > 0 &&
        form.last_name.trim().length > 0 &&
        form.birthdate.trim().length > 0 &&
        form.company.trim().length > 0 &&
        form.phone.trim().length > 0 &&
        /^\S+@\S+\.\S+$/.test(form.email.trim()) &&
        form.password.length >= 8;

    // Birthdate bounds: today (latest) back to 90 years ago (earliest)
    const today = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    const maxBirthdate = iso(today);
    const minBirthdate = iso(
        new Date(today.getFullYear() - 90, today.getMonth(), today.getDate()),
    );

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!isValid) return;
        setError("");
        setSubmitting(true);
        try {
            await register({
                ...form,
                full_name: `${form.first_name.trim()} ${form.last_name.trim()}`,
                company: form.company.trim(),
                phone: form.phone.trim(),
                home_address: form.home_address.trim(),
                email: form.email.trim().toLowerCase(),
            });
            if (pictureData) {
                try {
                    await updateProfilePicture(pictureData);
                } catch (picErr) {
                    // Non-fatal — account is created, user can upload later.
                    // eslint-disable-next-line no-console
                    console.warn("Post-register picture upload failed:", picErr);
                }
            }
            toast.success("Account created");
            // iter-108: prompt for GPS permission at signup — fires the
            // native browser prompt once so hour-log clock-in/out can
            // stamp coords later without re-asking. Best-effort; ignored
            // on decline / unsupported browsers.
            try { await requestLocationPermission(); } catch { /* ignore */ }
            navigate("/home", { replace: true });
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="relative h-[100dvh] w-full overflow-y-auto">
            <BlueprintBackground />
            <div className="relative z-10 min-h-full w-full flex items-start sm:items-center justify-center px-4 py-6">
                <div className="w-full max-w-[520px]">
                    <div className="flex justify-center mb-3">
                        <BrandLogo height={32} />
                    </div>

                    <div className="glass rounded-xl p-4" data-testid="register-panel">
                    <Link
                        to="/"
                        data-testid="link-back-login"
                        className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-white mb-2"
                    >
                        <ArrowLeft className="w-3.5 h-3.5" />
                        Back to sign in
                    </Link>
                    <h1 className="text-lg sm:text-xl font-bold text-white tracking-tight">
                        Create your account
                    </h1>
                    <p className="text-[11px] text-slate-400 mt-0.5 mb-3">
                        Tell us a bit about you and your company.
                    </p>

                    <div className="flex justify-center mb-3" data-testid="register-avatar-wrap">
                        <AvatarUpload
                            user={{ full_name: `${form.first_name} ${form.last_name}`.trim(), email: form.email }}
                            value={pictureData}
                            onPick={setPictureData}
                            sizeClass="w-16 h-16"
                            testId="register-avatar-upload"
                        />
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-2.5">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                            <Field id="first_name" label="First name" value={form.first_name} onChange={bind("first_name")} required autoComplete="given-name" />
                            <Field id="last_name" label="Last name" value={form.last_name} onChange={bind("last_name")} required autoComplete="family-name" />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                            <Field id="birthdate" label="Birthdate" type="date" value={form.birthdate} onChange={bind("birthdate")} required min={minBirthdate} max={maxBirthdate} />
                            <Field id="phone" label="Phone" type="tel" value={form.phone} onChange={bind("phone")} required autoComplete="tel" placeholder="e.g. 555-123-4567" />
                        </div>
                        <Field id="company" label="Company name" value={form.company} onChange={bind("company")} required autoComplete="organization" />
                        <Field id="home_address" label="Home address (optional)" value={form.home_address} onChange={bind("home_address")} autoComplete="street-address" placeholder="123 Main St, Toronto ON" />
                        <Field id="email" label="Email address" type="email" value={form.email} onChange={bind("email")} required autoComplete="email" placeholder="you@company.com" />
                        <div className="space-y-1">
                            <Label htmlFor="password" className="text-slate-300 text-[11px] uppercase tracking-wider">
                                Password <span className="text-slate-500 normal-case">(min 8)</span>
                            </Label>
                            <div className="relative">
                                <Input
                                    id="password"
                                    type={showPw ? "text" : "password"}
                                    value={form.password}
                                    onChange={bind("password")}
                                    data-testid="input-register-password"
                                    className="bg-[#0A1128]/70 border-[#2A3655] text-white placeholder:text-slate-500 focus-visible:ring-[#007AFF] pr-10 h-9"
                                    autoComplete="new-password"
                                    required
                                    minLength={8}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPw((s) => !s)}
                                    aria-label={showPw ? "Hide password" : "Show password"}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-sm flex items-center justify-center text-slate-400 hover:text-white"
                                >
                                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>

                        {error && (
                            <div
                                data-testid="register-error"
                                className="text-xs font-semibold text-red-300 bg-red-500/10 border border-red-500/30 rounded-sm px-3 py-1.5"
                            >
                                {error}
                            </div>
                        )}

                        <Button
                            type="submit"
                            disabled={!isValid || submitting}
                            data-testid="btn-register"
                            className="w-full bg-[#007AFF] hover:bg-[#3395FF] text-white font-bold h-10 disabled:opacity-40 disabled:bg-[#2A3655] disabled:text-slate-400"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Creating account…
                                </>
                            ) : (
                                <>
                                    Create account
                                    <ChevronRight className="w-4 h-4 ml-1.5" />
                                </>
                            )}
                        </Button>

                        <p className="text-[10px] text-slate-500 text-center">
                            Passwords are hashed with bcrypt. Your info stays private.
                        </p>
                    </form>
                </div>
                </div>
            </div>
        </div>
    );
}

function Field({ id, label, type = "text", value, onChange, required, autoComplete, placeholder, min, max }) {
    return (
        <div className="space-y-1">
            <Label htmlFor={id} className="text-slate-300 text-[11px] uppercase tracking-wider">
                {label}
            </Label>
            <Input
                id={id}
                type={type}
                value={value}
                onChange={onChange}
                required={required}
                autoComplete={autoComplete}
                placeholder={placeholder}
                min={min}
                max={max}
                data-testid={`input-register-${id}`}
                className="bg-[#0A1128]/70 border-[#2A3655] text-white placeholder:text-slate-500 focus-visible:ring-[#007AFF] h-9"
            />
        </div>
    );
}
